import { Store } from "../state/store";
import { ALLOWED_EXT, ALLOWED_MIME, MAX_FILE_BYTES, UploadMeta } from "../state/schema";
import { startUpload } from "../api/client";
import { formatNum } from "../logic/format";
import { h } from "./ui";

/* Upload-Block je Kategorie (§26). Mehrere Dateien möglich.
   Status: pending/uploading/done/error -> spiegelt die Checkliste wider. */

function sanitizeName(name: string): string {
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "") : "";
  let base = (dot >= 0 ? name.slice(0, dot) : name)
    .normalize("NFKD")
    .replace(/[^\w\- ]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 80);
  if (!base) base = "datei";
  return ext ? `${base}.${ext}` : base;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_BYTES) return `«${file.name}» supera los 15 MB.`;
  const ext = extOf(file.name);
  const mimeOk = !file.type || ALLOWED_MIME.includes(file.type.toLowerCase());
  const extOk = ALLOWED_EXT.includes(ext);
  if (!extOk || !mimeOk) return `«${file.name}» tiene un formato no admitido. Se permiten: PDF, JPG, PNG, HEIC, HEIF.`;
  return null;
}

interface UploadOpts {
  kategorie: string;
  multiple?: boolean;
  onChange?: () => void;
}

export function uploadBlock(store: Store, opts: UploadOpts): HTMLElement {
  const list = h("div", { class: "filelist" });
  const errLine = h("div", { class: "field__optional", style: { color: "var(--neg)" } });

  const renderList = () => {
    list.innerHTML = "";
    const items = store.state.dokumente.filter((d) => d.kategorie === opts.kategorie);
    for (const item of items) list.appendChild(renderItem(item));
  };

  const renderItem = (item: UploadMeta): HTMLElement => {
    const statusEl =
      item.status === "done"
        ? h("span", { class: "fileitem__status fileitem__status--ok", text: "✓ subido" })
        : item.status === "uploading"
        ? h("span", { class: "fileitem__status fileitem__status--up", text: `${item.progress}%` })
        : item.status === "error"
        ? h("span", { class: "fileitem__status fileitem__status--err", text: "falló" })
        : h("span", { class: "fileitem__status", text: "…" });

    const bar = h("div", { class: "fileitem__bar" }, h("i", { style: { width: `${item.progress}%` } }));
    const remove = h("button", {
      class: "btn-remove",
      type: "button",
      text: "Quitar",
      on: {
        click: () => {
          store.update((s) => {
            s.dokumente = s.dokumente.filter((d) => d.id !== item.id);
          }, false);
          renderList();
          opts.onChange?.();
        },
      },
    });
    return h(
      "div",
      { class: "fileitem" },
      h("span", { class: "fileitem__name", text: `${item.originalName} · ${formatNum(Math.round(item.size / 1024))} KB` }),
      item.status === "uploading" ? bar : null,
      statusEl,
      remove
    );
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    errLine.textContent = "";
    const arr = Array.from(files);
    const toAdd = opts.multiple === false ? arr.slice(0, 1) : arr;
    for (const file of toAdd) {
      const err = validateFile(file);
      if (err) {
        errLine.textContent = err;
        continue;
      }
      const meta: UploadMeta = {
        id: "u-" + Math.random().toString(36).slice(2, 10),
        kategorie: opts.kategorie,
        originalName: file.name,
        safeName: sanitizeName(file.name),
        mime: file.type || "application/octet-stream",
        size: file.size,
        status: "uploading",
        progress: 0,
      };
      store.update((s) => s.dokumente.push(meta), false);
      renderList();

      const uploadCtx = {
        kategorie: opts.kategorie,
        vorgangsnummer: store.state.meta.vorgangsnummer,
        kundenname: `${store.state.stammdaten.vorname} ${store.state.stammdaten.nachname}`.trim(),
      };
      const { promise } = startUpload(file, uploadCtx, (pct) => {
        store.update((s) => {
          const m = s.dokumente.find((d) => d.id === meta.id);
          if (m) m.progress = pct;
        }, false);
        renderList();
      });
      promise
        .then((res) => {
          store.update((s) => {
            const m = s.dokumente.find((d) => d.id === meta.id);
            if (m) {
              m.status = "done";
              m.progress = 100;
              m.driveFileId = res.driveFileId;
              m.uploadedAt = new Date().toISOString();
            }
          }, false);
          renderList();
          opts.onChange?.();
        })
        .catch((err) => {
          // Konkrete Ursache sichtbar machen (CORS/Netzwerk vs. Auth vs. finalize),
          // damit Fehler beim Live-Test eindeutig diagnostizierbar sind.
          console.error("[upload]", err);
          errLine.textContent = err instanceof Error && err.message ? err.message : "La subida falló.";
          store.update((s) => {
            const m = s.dokumente.find((d) => d.id === meta.id);
            if (m) m.status = "error";
          }, false);
          renderList();
          opts.onChange?.();
        });
    }
  };

  const input = h("input", {
    type: "file",
    attrs: {
      accept: ".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/*",
      ...(opts.multiple === false ? {} : { multiple: "" }),
    },
    on: { change: (e: Event) => handleFiles((e.target as HTMLInputElement).files) },
  });

  const drop = h(
    "label",
    {
      class: "upload",
      on: {
        dragover: (e: DragEvent) => { e.preventDefault(); (e.currentTarget as HTMLElement).style.borderColor = "var(--accent)"; },
        dragleave: (e: DragEvent) => { (e.currentTarget as HTMLElement).style.borderColor = ""; },
        drop: (e: DragEvent) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).style.borderColor = "";
          handleFiles(e.dataTransfer?.files ?? null);
        },
      },
    },
    h("div", { class: "upload__icon", text: "⬆️" }),
    h("div", { html: "<strong>Elegir archivo</strong> o arrástralo aquí" }),
    h("div", { class: "field__optional", text: "PDF, JPG, PNG, HEIC, HEIF · máx. 15 MB por archivo" }),
    input
  );

  renderList();
  return h("div", {}, drop, errLine, list);
}

/** Zählt erfolgreich hochgeladene Dokumente einer Kategorie. */
export function countDoneDocs(store: Store, kategorie: string): number {
  return store.state.dokumente.filter((d) => d.kategorie === kategorie && d.status === "done").length;
}
